import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { format } from "date-fns";
import {
  Calendar as CalendarIcon,
  SlidersHorizontal,
  MoreHorizontal,
  Eye,
  Trash,
  FileText,
  CheckCircle,
  CircleAlert,
  Filter,
  X,
  Loader2,
  Plus,
  Send,
  TruckIcon,
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { navigate } from "wouter/use-browser-location";
import { useDeliveryOrders, useDeleteDeliveryOrder, useCloneDeliveryOrder, useSendDeliveryOrderEmail } from "@/hooks/useDeliveryOrders";
import { DeliveryOrderListItem, DeliveryOrderStatus } from "@/types/salesDocuments";
import { useDecimalPrecision } from "@/hooks/useDecimalPrecision";

const cn = (...classes: (string | boolean)[]) =>
  classes.filter(Boolean).join(" ");

export const DeliveryOrders = () => {
  const { formatTotalAmount } = useDecimalPrecision();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [filter, setFilter] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState("all");
  const [dateRange, setDateRange] = React.useState<{
    from: Date | undefined;
    to: Date | undefined;
  }>({
    from: undefined,
    to: undefined,
  });
  const [selectedDeliveryOrders, setSelectedDeliveryOrders] = React.useState<string[]>([]);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [visibleColumns, setVisibleColumns] = React.useState({
    codeNumber: true,
    buyer: true,
    salesOrderNumber: true,
    deliveryDate: true,
    trackingNumber: true,
    carrierName: true,
    amount: true,
    status: true,
    emailStatus: true,
    actions: true,
  });
  const [dropdownOpenVisibleColumn, setDropdownOpenVisibleColumn] = React.useState(false);
  const [openDropdownRowId, setOpenDropdownRowId] = React.useState<string | null>(null);

  const itemsPerPage = 10;

  const { data: deliveryOrdersData, isLoading } = useDeliveryOrders();
  const deleteDeliveryOrderMutation = useDeleteDeliveryOrder();
  const cloneDeliveryOrderMutation = useCloneDeliveryOrder();
  const sendEmailMutation = useSendDeliveryOrderEmail();

  const deliveryOrders = deliveryOrdersData?.data || [];

  const statuses = [
    "all",
    DeliveryOrderStatus.DRAFT,
    DeliveryOrderStatus.PENDING,
    DeliveryOrderStatus.IN_TRANSIT,
    DeliveryOrderStatus.DELIVERED,
    DeliveryOrderStatus.CANCELLED,
  ];

  // Reset pagination when filters change
  const resetPagination = React.useCallback(() => {
    setCurrentPage(1);
  }, []);

  React.useEffect(() => {
    resetPagination();
  }, [filter, statusFilter, dateRange]);

  // Enhanced filter logic
  const filteredData = deliveryOrders.filter((deliveryOrder: DeliveryOrderListItem) => {
    const buyerName = deliveryOrder.buyerName ?? "Not Found";

    const matchesSearch =
      buyerName.toLowerCase().includes(filter.toLowerCase()) ||
      deliveryOrder.codeNumber.toLowerCase().includes(filter.toLowerCase()) ||
      deliveryOrder.salesOrderNumber.toLowerCase().includes(filter.toLowerCase()) ||
      deliveryOrder.trackingNumber?.toLowerCase().includes(filter.toLowerCase());

    const matchesStatus =
      statusFilter === "all" || deliveryOrder.status === statusFilter;

    const deliveryDate = new Date(deliveryOrder.deliveryDate);
    const matchesDateRange =
      (!dateRange.from || deliveryDate >= dateRange.from) &&
      (!dateRange.to || deliveryDate <= dateRange.to);

    return matchesSearch && matchesStatus && matchesDateRange;
  });

  // Calculate statistics
  const stats = {
    totalAmount: filteredData.reduce(
      (sum: number, do_: DeliveryOrderListItem) => sum + Number(do_.totalPayableAmount),
      0
    ),
    totalDeliveryOrders: filteredData.length,
    pendingDeliveryOrders: filteredData.filter(
      (do_: DeliveryOrderListItem) => do_.status === DeliveryOrderStatus.PENDING
    ).length,
    inTransitDeliveryOrders: filteredData.filter(
      (do_: DeliveryOrderListItem) => do_.status === DeliveryOrderStatus.IN_TRANSIT
    ).length,
    deliveredDeliveryOrders: filteredData.filter(
      (do_: DeliveryOrderListItem) => do_.status === DeliveryOrderStatus.DELIVERED
    ).length,
  };

  // Pagination logic
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedDeliveryOrders(filteredData.map((do_: DeliveryOrderListItem) => do_.id));
    } else {
      setSelectedDeliveryOrders([]);
    }
  };

  const handleSelectDeliveryOrder = (checked: string | boolean, id: string) => {
    if (checked) {
      setSelectedDeliveryOrders([...selectedDeliveryOrders, id]);
    } else {
      setSelectedDeliveryOrders(selectedDeliveryOrders.filter((selectedId) => selectedId !== id));
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteDeliveryOrderMutation.mutateAsync(id);
      toast({
        title: "Deleted",
        description: "Delivery Order has been deleted",
        icon: <CheckCircle size={30} className="animate-spin-once text-green-600" />,
        duration: 5000,
      });
      queryClient.invalidateQueries({ queryKey: ["deliveryOrders"] });
    } catch (error: any) {
      toast({
        title: "Action failed",
        variant: "destructive",
        description: error.response?.data?.message || "Failed to delete delivery order",
        icon: <CircleAlert size={30} className="animate-spin-once" />,
        duration: 5000,
      });
    }
  };

  const handleClone = async (id: string) => {
    try {
      await cloneDeliveryOrderMutation.mutateAsync(id);
      toast({
        title: "Success",
        description: "Delivery Order has been cloned",
        icon: <CheckCircle size={30} className="animate-spin-once text-green-600" />,
        duration: 5000,
      });
      queryClient.invalidateQueries({ queryKey: ["deliveryOrders"] });
    } catch (error: any) {
      toast({
        title: "Action failed",
        variant: "destructive",
        description: error.response?.data?.message || "Failed to clone delivery order",
        icon: <CircleAlert size={30} className="animate-spin-once" />,
        duration: 5000,
      });
    }
  };

  const handleBatchClone = async () => {
    let successCount = 0;
    for (const id of selectedDeliveryOrders) {
      try {
        await cloneDeliveryOrderMutation.mutateAsync(id);
        successCount++;
      } catch {
        // continue
      }
    }
    toast({
      title: "Clone Complete",
      description: `${successCount} delivery order(s) cloned successfully`,
      icon: <CheckCircle size={30} className="animate-spin-once text-green-600" />,
      duration: 5000,
    });
    queryClient.invalidateQueries({ queryKey: ["deliveryOrders"] });
    setSelectedDeliveryOrders([]);
  };

  const handleBatchSendEmail = async () => {
    try {
      await sendEmailMutation.mutateAsync(selectedDeliveryOrders);
      toast({
        title: "Sending",
        description: `Sending email for ${selectedDeliveryOrders.length} delivery order(s)`,
        icon: <CheckCircle size={30} className="animate-spin-once text-green-600" />,
        duration: 5000,
      });
      setSelectedDeliveryOrders([]);
    } catch (error: any) {
      toast({
        title: "Action failed",
        variant: "destructive",
        description: error.response?.data?.message || "Failed to send emails",
        icon: <CircleAlert size={30} className="animate-spin-once" />,
        duration: 5000,
      });
    }
  };

  const getEmailStatusColor = (status: string | null) => {
    const base = "rounded-full px-3 py-1 text-xs font-medium";
    switch (status) {
      case "Sending": return `${base} bg-yellow-100 text-yellow-800`;
      case "Sent": return `${base} bg-green-100 text-green-800`;
      case "Error": return `${base} bg-red-100 text-red-800`;
      default: return `${base} bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300`;
    }
  };

  const getStatusColor = (status: DeliveryOrderStatus) => {
    const baseStyle = "rounded-full px-3 py-1 text-xs font-medium";
    switch (status) {
      case DeliveryOrderStatus.DRAFT:
        return `${baseStyle} bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300`;
      case DeliveryOrderStatus.PENDING:
        return `${baseStyle} bg-yellow-100 text-yellow-800`;
      case DeliveryOrderStatus.IN_TRANSIT:
        return `${baseStyle} bg-blue-100 text-blue-800`;
      case DeliveryOrderStatus.DELIVERED:
        return `${baseStyle} bg-green-100 text-green-800`;
      case DeliveryOrderStatus.CANCELLED:
        return `${baseStyle} bg-red-100 text-red-800`;
      default:
        return `${baseStyle} bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300`;
    }
  };

  const getPaginationButtons = () => {
    const buttons = [];
    const maxVisibleButtons = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisibleButtons / 2));
    const endPage = Math.min(totalPages, startPage + maxVisibleButtons - 1);

    if (endPage - startPage < maxVisibleButtons - 1) {
      startPage = Math.max(1, endPage - maxVisibleButtons + 1);
    }

    if (startPage > 1) {
      buttons.push(
        <Button
          key="1"
          variant={"outline"}
          size="sm"
          onClick={() => setCurrentPage(1)}
        >
          1
        </Button>
      );

      if (startPage > 2) {
        buttons.push(<span key="ellipsis-start">...</span>);
      }
    }

    for (let page = startPage; page <= endPage; page++) {
      buttons.push(
        <Button
          key={page}
          variant={currentPage === page ? "default" : "outline"}
          size="sm"
          onClick={() => setCurrentPage(page)}
        >
          {page}
        </Button>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        buttons.push(<span key="ellipsis-end">...</span>);
      }

      buttons.push(
        <Button
          key={totalPages}
          variant={"outline"}
          size="sm"
          onClick={() => setCurrentPage(totalPages)}
        >
          {totalPages}
        </Button>
      );
    }

    return buttons;
  };

  const activeFilters = [
    statusFilter !== "all" && { type: "Status", value: statusFilter },
    dateRange.from && { type: "From", value: format(dateRange.from, "PP") },
    dateRange.to && { type: "To", value: format(dateRange.to, "PP") },
  ].filter(Boolean);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="w-full space-y-6 p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Delivery Orders</h1>
        <Button onClick={() => navigate("/delivery-order/new")}>
          <Plus className="mr-2 h-4 w-4" />
          Create Delivery Order
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        <Card className="shadow-md">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total DO Amount</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              RM {formatTotalAmount(stats.totalAmount)}
            </div>
            <p className="text-xs text-muted-foreground">Total delivery order value</p>
          </CardContent>
        </Card>

        <Card className="shadow-md">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total DOs</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalDeliveryOrders}</div>
            <p className="text-xs text-muted-foreground">Across all statuses</p>
          </CardContent>
        </Card>

        <Card className="shadow-md">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.pendingDeliveryOrders}</div>
            <p className="text-xs text-muted-foreground">Awaiting dispatch</p>
          </CardContent>
        </Card>

        <Card className="shadow-md">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">In Transit</CardTitle>
            <TruckIcon className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.inTransitDeliveryOrders}</div>
            <p className="text-xs text-muted-foreground">On the way</p>
          </CardContent>
        </Card>

        <Card className="shadow-md">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Delivered</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.deliveredDeliveryOrders}</div>
            <p className="text-xs text-muted-foreground">Successfully delivered</p>
          </CardContent>
        </Card>
      </div>

      {/* Filter Controls */}
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div className="flex flex-1 items-center gap-4">
            <Input
              placeholder="Search DO number, buyer, SO number, tracking..."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="max-w-sm"
            />

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {statuses
                  .filter((status) => status !== "all")
                  .map((status) => (
                    <SelectItem key={status} value={status}>
                      {status}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>

            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-[240px] justify-start text-left font-normal",
                    !dateRange.from && !dateRange.to && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dateRange.from ? (
                    dateRange.to ? (
                      <>
                        {format(dateRange.from, "PP")} - {format(dateRange.to, "PP")}
                      </>
                    ) : (
                      format(dateRange.from, "PP")
                    )
                  ) : (
                    "Pick a date range"
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  initialFocus
                  mode="range"
                  selected={dateRange}
                  onSelect={(range) =>
                    setDateRange({ from: range?.from, to: range?.to })
                  }
                  numberOfMonths={2}
                />
              </PopoverContent>
            </Popover>
          </div>

          <DropdownMenu
            open={dropdownOpenVisibleColumn}
            onOpenChange={(val) => setDropdownOpenVisibleColumn(val)}
          >
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="ml-4">
                <SlidersHorizontal className="mr-2 h-4 w-4" />
                Columns
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {Object.entries(visibleColumns).map(([key, value]) => (
                <DropdownMenuCheckboxItem
                  key={key}
                  className="capitalize"
                  checked={value}
                  onCheckedChange={(checked) =>
                    setVisibleColumns((prev) => ({ ...prev, [key]: checked }))
                  }
                >
                  {key}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Batch actions */}
        <div className="h-9">
          {selectedDeliveryOrders.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                {selectedDeliveryOrders.length} selected
              </span>
              <Button
                size="sm"
                variant="outline"
                onClick={handleBatchSendEmail}
                disabled={sendEmailMutation.isPending}
              >
                {sendEmailMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Send className="mr-2 h-4 w-4" />
                )}
                Send Email
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleBatchClone}
                disabled={cloneDeliveryOrderMutation.isPending}
              >
                {cloneDeliveryOrderMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Plus className="mr-2 h-4 w-4" />
                )}
                Clone
              </Button>
            </div>
          )}
        </div>

        {/* Active Filters */}
        {activeFilters.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {activeFilters.map((filter) => (
              <Badge
                key={filter ? `${filter.type}-${filter.value}` : undefined}
                variant="secondary"
                className="flex items-center gap-1"
              >
                {filter && `${filter.type}: ${filter.value}`}
                <X
                  className="h-3 w-3 cursor-pointer"
                  onClick={() => {
                    if (filter) {
                      switch (filter.type) {
                        case "Status":
                          setStatusFilter("all");
                          break;
                        case "From":
                        case "To":
                          setDateRange({ from: undefined, to: undefined });
                          break;
                      }
                    }
                  }}
                />
              </Badge>
            ))}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setStatusFilter("all");
                setDateRange({ from: undefined, to: undefined });
                setFilter("");
              }}
            >
              Clear all filters
            </Button>
          </div>
        )}
      </div>

      {/* Empty State */}
      {filteredData.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed p-8 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-gray-100">
            <Filter className="h-6 w-6 text-gray-400" />
          </div>
          <h3 className="mt-4 font-semibold">No delivery orders found</h3>
          <p className="mt-2 text-sm text-gray-500">
            {filter || statusFilter !== "all" || dateRange.from
              ? "Try adjusting your search or filter to find what you're looking for."
              : "Get started by creating your first delivery order."}
          </p>
          {!filter && statusFilter === "all" && !dateRange.from && (
            <Button
              className="mt-4"
              onClick={() => navigate("/delivery-order/new")}
            >
              <Plus className="mr-2 h-4 w-4" />
              Create Delivery Order
            </Button>
          )}
        </div>
      )}

      {/* Table */}
      {filteredData.length > 0 && (
        <>
          <div className="rounded-md border shadow-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={
                        selectedDeliveryOrders.length === filteredData.length &&
                        selectedDeliveryOrders.length !== 0
                      }
                      onCheckedChange={handleSelectAll}
                    />
                  </TableHead>
                  {visibleColumns.codeNumber && <TableHead>DO Number</TableHead>}
                  {visibleColumns.buyer && <TableHead>Buyer</TableHead>}
                  {visibleColumns.salesOrderNumber && <TableHead>SO Number</TableHead>}
                  {visibleColumns.deliveryDate && <TableHead>Delivery Date</TableHead>}
                  {visibleColumns.trackingNumber && <TableHead>Tracking Number</TableHead>}
                  {visibleColumns.carrierName && <TableHead>Carrier</TableHead>}
                  {visibleColumns.amount && <TableHead className="text-right">Amount</TableHead>}
                  {visibleColumns.status && <TableHead>Status</TableHead>}
                  {visibleColumns.emailStatus && <TableHead>Email Status</TableHead>}
                  {visibleColumns.actions && <TableHead>Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedData.map((deliveryOrder: DeliveryOrderListItem) => (
                  <TableRow
                    key={deliveryOrder.id}
                    onDoubleClick={() => navigate(`/delivery-order/view`, { state: { id: deliveryOrder.id } })}
                  >
                    <TableCell>
                      <Checkbox
                        checked={selectedDeliveryOrders.includes(deliveryOrder.id)}
                        onCheckedChange={(checked) =>
                          handleSelectDeliveryOrder(checked, deliveryOrder.id)
                        }
                      />
                    </TableCell>
                    {visibleColumns.codeNumber && (
                      <TableCell className="font-medium">{deliveryOrder.codeNumber}</TableCell>
                    )}
                    {visibleColumns.buyer && (
                      <TableCell>
                        {deliveryOrder.buyerName || "Not Found"}
                        <div className="text-xs text-gray-500">{deliveryOrder.buyerCode}</div>
                      </TableCell>
                    )}
                    {visibleColumns.salesOrderNumber && (
                      <TableCell>{deliveryOrder.salesOrderNumber}</TableCell>
                    )}
                    {visibleColumns.deliveryDate && (
                      <TableCell>
                        {format(new Date(deliveryOrder.deliveryDate), "dd/MM/yyyy")}
                      </TableCell>
                    )}
                    {visibleColumns.trackingNumber && (
                      <TableCell>{deliveryOrder.trackingNumber || "-"}</TableCell>
                    )}
                    {visibleColumns.carrierName && (
                      <TableCell>{deliveryOrder.carrierName || "-"}</TableCell>
                    )}
                    {visibleColumns.amount && (
                      <TableCell className="text-right">
                        {formatTotalAmount(Number(deliveryOrder.totalPayableAmount))}
                      </TableCell>
                    )}
                    {visibleColumns.status && (
                      <TableCell>
                        <span className={getStatusColor(deliveryOrder.status)}>
                          {deliveryOrder.status}
                        </span>
                      </TableCell>
                    )}
                    {visibleColumns.emailStatus && (
                      <TableCell>
                        {deliveryOrder.emailStatus ? (
                          <span className={getEmailStatusColor(deliveryOrder.emailStatus)}>
                            {deliveryOrder.emailStatus}
                          </span>
                        ) : null}
                      </TableCell>
                    )}
                    {visibleColumns.actions && (
                      <TableCell>
                        <DropdownMenu
                          open={openDropdownRowId === deliveryOrder.id}
                          onOpenChange={(isOpen) =>
                            setOpenDropdownRowId(isOpen ? deliveryOrder.id : null)
                          }
                        >
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-8 w-8 p-0">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <Button
                              variant="ghost"
                              onClick={() => navigate(`/delivery-order/view`, { state: { id: deliveryOrder.id } })}
                            >
                              <Eye className="mr-2 h-4 w-4" />
                              View Details
                            </Button>
                            <DropdownMenuSeparator />
                            <Button
                              variant="ghost"
                              className="w-full justify-start"
                              onClick={() => handleClone(deliveryOrder.id)}
                            >
                              <Plus className="mr-2 h-4 w-4" />
                              Clone
                            </Button>
                            <DropdownMenuSeparator />
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  className="w-full justify-start text-red-600"
                                  variant="ghost"
                                >
                                  <Trash className="mr-2 h-4 w-4" />
                                  Delete
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete Delivery Order</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Are you sure you want to delete delivery order{" "}
                                    {deliveryOrder.codeNumber}? This action cannot be undone.
                                    <span className="block mt-2 text-sm">
                                      Note: Stock quantities will be restored and the linked sales order status will be updated.
                                    </span>
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleDelete(deliveryOrder.id)}
                                  >
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination Controls */}
          <div className="flex items-center justify-between px-2">
            <div className="text-sm text-gray-500">
              Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
              {Math.min(currentPage * itemsPerPage, filteredData.length)} of{" "}
              {filteredData.length} results
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              {getPaginationButtons()}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DeliveryOrders;
